import type { Ref } from 'vue';
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getPortfolioRisk } from '@/services/reportsService';
import { PaymentStatus, type PaymentStatusValue } from '@/services/paymentStatus';

interface RiskCategory {
    id: string;
    label: string;
    color: string;
    count: number;
    percentage: number;
}

interface RiskCategoryDefinition {
    id: string;
    label: string;
    color: string;
}

const CATEGORY_DEFS: RiskCategoryDefinition[] = [
    { id: 'on-time', label: 'All previous payments on time', color: '#00D26A' },
    { id: 'late-payment', label: 'At least one payment canceled', color: '#FC7C19' },
    { id: 'low-balance', label: 'Low Balance / Not Sufficient Funds', color: '#A61B78' },
];

type PaymentsTarget = { tab: string; status?: PaymentStatusValue[] };

const CATEGORY_TARGET: Record<string, PaymentsTarget> = {
    'on-time': { tab: 'deposits' },
    'late-payment': { tab: 'other', status: [PaymentStatus.CANCEL] },
    'low-balance': { tab: 'other', status: [PaymentStatus.LOW_BALANCE] },
};

// Named composable export keeps existing imports stable.
// eslint-disable-next-line import/prefer-default-export
export function useReportsPortfolioRisk(dateRange: Ref<[string, string]>) {
    const router = useRouter();
    const loading = ref(true);
    const categories = ref<RiskCategory[]>([]);
    const total = ref(0);

    const chartData = computed(() => ({
        labels: categories.value.map((c) => c.label),
        datasets: [{
            data: categories.value.map((c) => c.count),
            backgroundColor: categories.value.map((c) => c.color),
            borderWidth: 6,
            borderColor: '#ffffff',
            hoverBorderWidth: 6,
            hoverBorderColor: '#ffffff',
            hoverOffset: 4,
        }],
    }));

    const percentages = computed(() => (
        categories.value.map((c) => ({
            ...c,
            percent: c.percentage.toFixed(1).replace(/\.0$/, ''),
        }))
    ));

    const openCategory = (index: number) => {
        const category = categories.value[index];
        const target = category ? CATEGORY_TARGET[category.id] : undefined;
        if (!target) return;

        const [from, to] = dateRange.value;
        router.push({
            path: '/payments',
            query: {
                tab: target.tab,
                ...(target.status ? { status: target.status } : {}),
                from,
                to,
            },
        });
    };

    watch(dateRange, async (_val, _old, onCleanup) => {
        let cancelled = false;
        onCleanup(() => { cancelled = true; });

        loading.value = true;
        try {
            const [from, to] = dateRange.value;
            const data = await getPortfolioRisk(from, to);
            if (cancelled) return;
            total.value = data.total;
            categories.value = [
                {
                    ...CATEGORY_DEFS[0],
                    count: data.onTimeTransactions,
                    percentage: data.onTimePercentage,
                },
                {
                    ...CATEGORY_DEFS[1],
                    count: data.atLeastOneLateTransactions,
                    percentage: data.atLeastOneLatePercentage,
                },
                {
                    ...CATEGORY_DEFS[2],
                    count: data.lowBalanceTransactions,
                    percentage: data.lowBalancePercentage,
                },
            ];
        } finally {
            if (!cancelled) loading.value = false;
        }
    }, { immediate: true, deep: true });

    return {
        loading,
        categories,
        chartData,
        percentages,
        total,
        openCategory,
    };
}
